import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { CoiHeaderService, CoiHeader, CoiHeaderDto } from '../../services/coi-header';
import { ClientService, Client } from '../../services/client';
import { MasterPolicyService, MasterPolicy } from '../../services/master-policy';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

type ScopeMode = 'MAIN' | 'CLIENT';

/**
 * COI Header — single-table admin CRUD for the issuer / header block printed on the Certificate of
 * Insurance. Main / Partner (clientId == null) is the default for ALL partners; client-specific rows
 * override it for that client's members. Every field is an optional override — a blank field falls
 * back to the member's dynamic value, so a blank row renders the certificate exactly as before.
 * Deletes confirm via SweetAlert2 (ToastService), matching the other masters.
 */
@Component({
  selector: 'app-coi-header',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    TextareaModule,
    ToggleSwitchModule,
  ],
  templateUrl: './coi-header.html',
  styleUrls: ['./coi-header.css'],
})
export class CoiHeaderPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(CoiHeaderService);
  private clientSvc = inject(ClientService);
  private policySvc = inject(MasterPolicyService);
  private route = inject(ActivatedRoute);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  /** When true, the dialog's scope+client are fixed (opened from the premium-assign flow). */
  clientLocked = signal(false);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  rows = signal<CoiHeader[]>([]);

  scopeMode = signal<ScopeMode>('MAIN');
  readonly scopeOptions = [
    { value: 'MAIN' as ScopeMode, label: 'Main / Partner (default)' },
    { value: 'CLIENT' as ScopeMode, label: 'Specific client' },
  ];

  clientOptions = signal<{ value: number; label: string }[]>([]);
  clientsLoaded = signal(false);

  /** Master policies of the client currently selected in the form (client-owned only). */
  masterPolicyOptions = signal<{ value: number; label: string }[]>([]);

  filterClientId = signal<number | null>(null);

  /** Main / Partner first, then by client name. */
  visible = computed(() =>
    [...this.rows()].sort((a, b) => {
      const am = a.clientId == null ? 0 : 1;
      const bm = b.clientId == null ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.clientName ?? '').localeCompare(b.clientName ?? '');
    }),
  );

  form: FormGroup = this.fb.group({
    id: [0],
    clientId: [null as number | null],
    masterPolicyId: [null as number | null],
    certificateNo: [''],
    masterPolicyNo: [''],
    insuranceCompany: [''],
    mainCompany: [''],
    policyServingOffice: [''],
    insuredName: [''],
    insuredAddress: [''],
    intermediaryName: [''],
    intermediaryCode: [''],
    periodofInsurance: [''],
    place: [''],
    remarks: [''],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    this.loadClients();
    // When the form's client changes, load that client's master policies (for the optional
    // master-policy scope) and drop any now-invalid selection.
    this.form.get('clientId')!.valueChanges.subscribe((cid: number | null) => {
      this.form.get('masterPolicyId')!.setValue(null);
      this.loadPolicies(cid);
    });
    // Deep link from the premium-assign flow: /masters/coi-header?add=1&clientId=&masterPolicyNo=&period=
    this.route.queryParamMap.subscribe((pm) => {
      if (pm.get('add') !== '1') return;
      const clientId = Number(pm.get('clientId'));
      if (!Number.isInteger(clientId) || clientId <= 0) return;
      this.openForClient(clientId, pm.get('masterPolicyNo') ?? '', pm.get('period') ?? '');
    });
  }

  /** Open the Add dialog pre-scoped to a client (locked) with master-policy prefill. */
  private openForClient(clientId: number, masterPolicyNo: string, period: string) {
    this.editing.set(false);
    this.scopeMode.set('CLIENT');
    this.clientLocked.set(true);
    this.form.reset({ ...this.blank(), clientId, masterPolicyNo, periodofInsurance: period, isActive: true });
    this.form.get('clientId')!.disable();
    this.showModal.set(true);
  }

  // ===================== data =====================
  load() {
    this.loading.set(true);
    const cid = this.filterClientId();
    const req = cid != null ? this.svc.byClient(cid) : this.svc.all();
    req.subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not load COI headers.');
          this.rows.set([]);
          return;
        }
        this.rows.set(res?.data ?? []);
      },
      error: () => this.loading.set(false),
    });
  }

  private loadClients() {
    this.clientSvc.cltGetAll().subscribe({
      next: (res) => {
        const list: Client[] = (res?.data ?? []) as Client[];
        this.clientOptions.set(
          list
            .filter((c) => c.isActive !== false)
            .map((c) => ({
              value: c.id,
              label: c.clientCode ? `${c.clientName} (${c.clientCode})` : c.clientName,
            })),
        );
        this.clientsLoaded.set(true);
      },
    });
  }

  clientLabel(id: number): string {
    return this.clientOptions().find((o) => o.value === id)?.label ?? `Client #${id}`;
  }

  /** Load a client's own master policies for the optional master-policy scope. */
  private loadPolicies(clientId: number | null, thenSelect?: number | null) {
    this.masterPolicyOptions.set([]);
    if (!clientId) return;
    this.policySvc.getAll({ ownerType: 'Client', ownerId: clientId }).subscribe({
      next: (res: any) => {
        const list: MasterPolicy[] = Array.isArray(res?.data) ? res.data : [];
        this.masterPolicyOptions.set(
          list.map((p) => ({
            value: p.id,
            label: p.masterPolicyNumber ? `${p.masterPolicyNumber} — ${p.masterPolicyName}` : (p.masterPolicyName ?? `Policy #${p.id}`),
          })),
        );
        if (thenSelect != null) this.form.get('masterPolicyId')!.setValue(thenSelect);
      },
    });
  }

  /** Grid scope label: Main / Partner, a client (client-wide), or a client + master policy. */
  scopeLabel(r: CoiHeader): string {
    if (r.clientId == null) return 'Main / Partner';
    const client = r.clientName ?? this.clientLabel(r.clientId);
    if (r.masterPolicyId) return `${client} · ${r.masterPolicyNumber ?? r.masterPolicyName ?? 'Master policy'}`;
    return client;
  }

  onFilterChange(clientId: number | null) {
    this.filterClientId.set(clientId ?? null);
    this.load();
  }

  clearFilter() {
    if (this.filterClientId() == null) return;
    this.filterClientId.set(null);
    this.load();
  }

  /** Short one-line preview of the issuer values for the row. */
  summary(r: CoiHeader): string {
    return [r.mainCompany, r.policyServingOffice, r.place].filter(Boolean).join(' · ') || '—';
  }

  // ===================== add / edit =====================
  onScopeChange(mode: ScopeMode) {
    if (this.clientLocked()) return;
    this.scopeMode.set(mode);
    if (mode === 'MAIN') this.form.get('clientId')!.setValue(null);
  }

  private blank() {
    return {
      id: 0,
      clientId: null,
      masterPolicyId: null,
      certificateNo: '',
      masterPolicyNo: '',
      insuranceCompany: '',
      mainCompany: '',
      policyServingOffice: '',
      insuredName: '',
      insuredAddress: '',
      intermediaryName: '',
      intermediaryCode: '',
      periodofInsurance: '',
      place: '',
      remarks: '',
      isActive: true,
    };
  }

  openAdd() {
    this.editing.set(false);
    this.scopeMode.set('MAIN');
    this.clientLocked.set(false);
    this.form.get('clientId')!.enable();
    this.form.reset(this.blank());
    this.showModal.set(true);
  }

  openEdit(r: CoiHeader) {
    this.editing.set(true);
    this.clientLocked.set(false);
    this.form.get('clientId')!.enable();
    this.scopeMode.set(r.clientId == null ? 'MAIN' : 'CLIENT');
    this.form.reset({
      id: r.id,
      clientId: r.clientId ?? null,
      masterPolicyId: null,     // restored by loadPolicies(thenSelect) below (after options load)
      certificateNo: r.certificateNo ?? '',
      masterPolicyNo: r.masterPolicyNo ?? '',
      insuranceCompany: r.insuranceCompany ?? '',
      mainCompany: r.mainCompany ?? '',
      policyServingOffice: r.policyServingOffice ?? '',
      insuredName: r.insuredName ?? '',
      insuredAddress: r.insuredAddress ?? '',
      intermediaryName: r.intermediaryName ?? '',
      intermediaryCode: r.intermediaryCode ?? '',
      periodofInsurance: r.periodofInsurance ?? '',
      place: r.place ?? '',
      remarks: r.remarks ?? '',
      isActive: r.isActive ?? true,
    });
    // Load this client's master policies, then restore the saved master-policy selection.
    if (r.clientId) this.loadPolicies(r.clientId, r.masterPolicyId ?? null);
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.clientLocked.set(false);
    this.form.get('clientId')!.enable();
  }

  save() {
    const v = this.form.getRawValue();
    if (this.scopeMode() === 'CLIENT' && !v.clientId) {
      this.toast.warning('Select a client for the client-specific header, or switch the scope to Main / Partner.');
      return;
    }
    // Mandatory when opened from the premium-assign flow: the client must have these.
    if (this.clientLocked()) {
      if (!(v.masterPolicyNo ?? '').toString().trim()) {
        this.toast.warning('Master Policy No is required.');
        return;
      }
      if (!(v.periodofInsurance ?? '').toString().trim()) {
        this.toast.warning('Period of Insurance is required.');
        return;
      }
    }

    const trim = (s: any): string | null => ((s ?? '').toString().trim() || null);
    const clientId = this.scopeMode() === 'CLIENT' ? Number(v.clientId) : null;
    // Master policy only applies to a client-scoped header; blank = client-wide.
    const masterPolicyId = clientId && v.masterPolicyId ? Number(v.masterPolicyId) : null;
    const dto: CoiHeaderDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId,
      masterPolicyId,
      certificateNo: trim(v.certificateNo),
      masterPolicyNo: trim(v.masterPolicyNo),
      insuranceCompany: trim(v.insuranceCompany),
      mainCompany: trim(v.mainCompany),
      policyServingOffice: trim(v.policyServingOffice),
      insuredName: trim(v.insuredName),
      insuredAddress: trim(v.insuredAddress),
      intermediaryName: trim(v.intermediaryName),
      intermediaryCode: trim(v.intermediaryCode),
      periodofInsurance: trim(v.periodofInsurance),
      place: trim(v.place),
      remarks: trim(v.remarks),
      isActive: !!v.isActive,
    };

    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    this.saving.set(true);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save COI header.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'COI header updated' : 'COI header created'));
        if (dto.isActive) this.warnDuplicateActiveScope(dto.clientId ?? null, dto.masterPolicyId ?? null, dto.id);
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  // ===================== enable / disable / delete =====================
  toggleActive(r: CoiHeader) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    const req = next ? this.svc.enable(r.id) : this.svc.disable(r.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
          return;
        }
        if (next) this.warnDuplicateActiveScope(r.clientId ?? null, r.masterPolicyId ?? null, r.id);
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<CoiHeader>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  /** Advisory hint only — the backend does NOT auto-deactivate siblings (highest active Id wins). */
  private warnDuplicateActiveScope(clientId: number | null, masterPolicyId: number | null, selfId: number) {
    const other = this.rows().some(
      (x) => x.id !== selfId && x.isActive && (x.clientId ?? null) === clientId && (x.masterPolicyId ?? null) === masterPolicyId,
    );
    if (!other) return;
    const scope = clientId == null
      ? 'Main / Partner'
      : masterPolicyId
        ? `${this.clientLabel(clientId)} · this master policy`
        : this.clientLabel(clientId);
    this.toast.info(
      `Another active header already exists for ${scope}. Only the latest active row is used — disable the old one to make this one take effect.`,
      'Heads up',
    );
  }

  async remove(r: CoiHeader) {
    const isMain = r.clientId == null;
    const scope = isMain ? 'the Main / Partner header' : `the header for ${r.clientName ?? this.clientLabel(r.clientId!)}`;
    const warn = isMain
      ? 'This permanently deletes the Main / Partner header. Certificates will fall back to the built-in defaults until you add another Main / Partner row.'
      : `This permanently deletes ${scope}. That client's certificates will fall back to the Main / Partner header.`;
    const ok = await this.toast.confirm(warn, 'Delete COI header?', 'Delete');
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete COI header.');
          return;
        }
        this.toast.success(res?.message || 'COI header deleted');
        this.load();
      },
    });
  }
}
